import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { SPEAKERS, SPEAKER_CATEGORIES, PDF_BG } from "../data/content";
import SectionBg from "./SectionBg";

const INITIAL_COLORS = [
    "from-[#FF6B35] to-[#9D4CDD]",
    "from-[#9D4CDD] to-[#FF6B35]",
    "from-[#FF6B35] to-[#FF8957]",
    "from-[#9D4CDD] to-[#6A2EAA]",
];

function initials(name = "") {
    return name
        .replace(/[^A-Za-z\s]/g, "")
        .trim()
        .split(/\s+/)
        .map((p) => p[0])
        .slice(0, 2)
        .join("")
        .toUpperCase() || "JAF";
}

export default function Speakers() {
    const speakers = SPEAKERS;
    const hasSpeakers = speakers.length > 0;

    return (
        <section
            id="speakers"
            className="relative py-24 md:py-40 overflow-hidden"
            data-testid="speakers-section"
        >
            <SectionBg src={PDF_BG.speakers} opacity={0.6} />
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
                <div className="grid grid-cols-12 gap-6 md:gap-12 items-center mb-12 md:mb-16">
                    <div className="col-span-12 md:col-span-7 min-w-0">
                        <div className="text-xs uppercase tracking-[0.3em] text-[#FF6B35] font-mono mb-6">The Lineup</div>
                        <h2 className="min-w-0 max-w-full font-display font-bold text-[1.62rem] leading-[1.14] tracking-normal break-words [overflow-wrap:anywhere] sm:text-5xl sm:leading-[1.05] sm:tracking-tight lg:text-6xl">
                            The names that <span className="text-stroke-orange">shape the year</span>.
                        </h2>
                        <p className="mt-5 sm:mt-6 max-w-xl text-sm sm:text-base md:text-lg text-zinc-300 leading-relaxed [overflow-wrap:anywhere]">
                            We're curating India's most decorated creative leaders, agency heads, brand CMOs, film
                            directors, top creators and global keynotes. Reveals begin Q2 2026 — here's a glimpse.
                        </p>
                    </div>
                    {/* <div className="col-span-12 md:col-span-5 grid grid-cols-2 gap-4">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="aspect-[3/4] rounded-3xl overflow-hidden border border-white/10 mt-8"
                        >
                            <img src={MEDIA.speaker1} alt="Speaker" className="w-full h-full object-cover" />
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.15 }}
                            className="aspect-[3/4] rounded-3xl overflow-hidden border border-white/10"
                        >
                            <img src={MEDIA.speaker2} alt="Speaker" className="w-full h-full object-cover" />
                        </motion.div>
                    </div> */}
                </div>

                {/* Speaker cards */}
                {hasSpeakers ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                        {speakers.map((s, i) => {
                            const isReveal = s.name.toLowerCase().includes("coming soon") || s.name.toLowerCase().includes("reveal");
                            return (
                                <motion.div
                                    key={s.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{ delay: (i % 10) * 0.05, duration: 0.6 }}
                                    whileHover={{ y: -6 }}
                                    className="group relative rounded-3xl border border-white/10 bg-[#141414] overflow-hidden hover:border-[#FF6B35]/60 transition-all duration-500"
                                    data-testid={`speaker-card-${i}`}
                                >
                                    <div className="aspect-[3/4] relative overflow-hidden">
                                        {s.photo_url ? (
                                            <img src={s.photo_url} alt={s.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className={`absolute inset-0 bg-gradient-to-br ${INITIAL_COLORS[i % INITIAL_COLORS.length]} opacity-90`}>
                                                <div className="absolute inset-0 bg-[#0a0a0a]/50" />
                                            </div>
                                        )}
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            {isReveal ? (
                                                <Sparkles size={56} className="text-white/70 group-hover:scale-110 transition" />
                                            ) : (
                                                !s.photo_url && (
                                                    <span className="font-display text-5xl md:text-6xl font-black text-white/30 group-hover:text-white/50 transition">
                                                        {initials(s.name)}
                                                    </span>
                                                )
                                            )}
                                        </div>
                                        <div className="absolute top-3 left-3">
                                            <span className="text-[10px] uppercase tracking-[0.2em] font-mono px-2 py-1 rounded-full bg-black/50 backdrop-blur-md text-[#FF6B35] border border-white/10">
                                                {s.category || "Speaker"}
                                            </span>
                                        </div>
                                        {s.featured && (
                                            <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-[#FF6B35] animate-pulse" />
                                        )}
                                    </div>
                                    <div className="p-4 md:p-5 border-t border-white/10">
                                        <div className="font-display text-base md:text-lg font-bold leading-tight line-clamp-2 [overflow-wrap:anywhere]">{s.name}</div>
                                        <div className="text-[11px] uppercase tracking-widest text-zinc-400 mt-1 line-clamp-2">{s.role}</div>
                                        {s.company && (
                                            <div className="text-xs text-zinc-500 mt-1 line-clamp-1">{s.company}</div>
                                        )}
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center py-16 rounded-3xl border border-white/10 bg-[#141414]/50 text-zinc-400">
                        Speaker lineup coming soon.
                    </div>
                )}

                {/* Category pills */}
                <div className="mt-16">
                    <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 font-mono mb-6">
                        Speaker Categories
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {SPEAKER_CATEGORIES.map((c, i) => (
                            <span
                                key={c}
                                className="px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] font-mono border border-white/15 text-zinc-300 bg-[#141414] hover:border-[#FF6B35]/60 transition cursor-default"
                                data-testid={`speaker-cat-${i}`}
                            >
                                {c}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
